/**
 * PWA Install Prompt Helper
 * Handles PWA installation prompts and detection
 */

/**
 * Check if app is running in standalone mode
 */
export function isStandalone() {
  const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true;
  
  // Track that user has visited in standalone mode
  if (standalone) {
    localStorage.setItem('has-visited-standalone', 'true');
    // Set PWA installed flag with timestamp (as per expert recommendation)
    localStorage.setItem('pwa_installed', 'yes');
    localStorage.setItem('pwa_installed_date', Date.now().toString());
  }
  
  return standalone;
}

/**
 * Check if device is iOS
 */
export function isIOSDevice() {
  // Modern iPads (iPadOS 13+) report as Macintosh, so also check touch + Mac
  return (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Check if device is Android
 */
export function isAndroidDevice() {
  return /Android/.test(navigator.userAgent);
}

/**
 * Track page visits for engagement metrics
 */
export function trackPageVisit() {
  const visitCount = parseInt(localStorage.getItem('visit-count') || '0');
  localStorage.setItem('visit-count', (visitCount + 1).toString());
  
  // Track session start time
  if (!sessionStorage.getItem('session-start')) {
    sessionStorage.setItem('session-start', Date.now().toString());
  }
}

/**
 * Get user engagement metrics
 */
export function getEngagementMetrics() {
  const visitCount = parseInt(localStorage.getItem('visit-count') || '0');
  const timeOnSite = parseInt(sessionStorage.getItem('time-on-site') || '0');
  const hasVisitedStandalone = localStorage.getItem('has-visited-standalone') === 'true';
  
  return {
    visitCount,
    timeOnSite,
    hasVisitedStandalone,
    isEngaged: visitCount >= 2 || timeOnSite > 30000 // 30 seconds
  };
}

/**
 * Check if browser supports PWA installation
 */
export function canInstallPWA() {
  // iOS can always "install" via Add to Home Screen
  if (isIOSDevice()) {
    return true;
  }
  
  // For other platforms, check for beforeinstallprompt support
  return 'BeforeInstallPromptEvent' in window || 
         'onbeforeinstallprompt' in window;
}

/**
 * Initialize PWA installation tracking
 * This replaces the old showPWAInstallPrompt function
 * @param {Object} config - Center configuration
 */
export function initializePWATracking(config) {
  // Track page visit
  trackPageVisit();
  
  // Track if running in standalone
  isStandalone();
  
  // Track PWA installation (this already exists)
  trackPWAInstallation();
  
  console.log('PWA tracking initialized for:', config?.name);
}

/**
 * Check if PWA is marked as installed (with TTL)
 */
export function isPWAInstalled() {
  const installed = localStorage.getItem('pwa_installed');
  const installDate = localStorage.getItem('pwa_installed_date');
  
  if (installed === 'yes' && installDate) {
    // Check if flag is older than 120 days (expert recommendation)
    const daysSinceInstall = (Date.now() - parseInt(installDate)) / (1000 * 60 * 60 * 24);
    if (daysSinceInstall > 120) {
      // Expired, clear flags
      localStorage.removeItem('pwa_installed');
      localStorage.removeItem('pwa_installed_date');
      return false;
    }
    return true;
  }
  
  return false;
}

/**
 * Mark PWA as installed (user indicated they have it)
 */
export function markPWAInstalled() {
  localStorage.setItem('pwa_installed', 'yes');
  localStorage.setItem('pwa_installed_date', Date.now().toString());
}

/**
 * Clear PWA installation flag (user correction)
 */
export function clearPWAInstalled() {
  localStorage.removeItem('pwa_installed');
  localStorage.removeItem('pwa_installed_date');
}

/**
 * Track PWA installation
 */
export function trackPWAInstallation() {
  window.addEventListener('appinstalled', (evt) => {
    console.log('PWA was installed successfully');
    markPWAInstalled();
  });
  
  // For iOS, we can't detect installation directly,
  // but we can check if running in standalone mode on load
  if (isStandalone() && isIOSDevice()) {
    console.log('iOS PWA is running in standalone mode');
  }
}